import React, { useState, useEffect } from "react";
import axios from "axios";
import baseUrl from "../../../helpers/baseUrl";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const GiftCardPage = () => {
  document.title = "Estarch | Gift Cards";

  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [formData, setFormData] = useState({
    code: "",
    amount: "",
    expiryDate: new Date(),
    isActive: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedGiftCard, setSelectedGiftCard] = useState(null);
  const [typeFormData, setTypeFormData] = useState({ amount: 0 });
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [giftCardTypes, setGiftCardTypes] = useState([]);

  useEffect(() => {
    fetchGiftCards();
    fetchGiftCardTypes();
  }, []);

  const fetchGiftCards = async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/api/giftcards`);
      setGiftCards(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching gift cards:", error);
    }
  };

  const fetchGiftCardTypes = async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/api/giftcards/get/gift-card-type`);
      setGiftCardTypes(data);
    } catch (error) {
      console.error("Error fetching gift card types:", error);
    }
  };

  const handleGiftCardTypesChange = (value) => {
    setGiftCardAmount(value);
    setFormData((prev) => ({ ...prev, amount: value })); // Update formData.amount
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await axios.post(`${baseUrl}/api/giftcards/create/gift-card-type`, typeFormData);
      setMessage(response.data.message);
      setTypeFormData({ amount: "" });
      fetchGiftCardTypes();
      setTypeModalOpen(false);
    } catch (error) {
      setMessage(error.response?.data?.message || "Error adding gift card type");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, expiryDate: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await axios.post(`${baseUrl}/api/giftcards`, formData);
      setMessage(response.data.message);
      setFormData({ code: "", amount: "", expiryDate: new Date(), isActive: true });
      fetchGiftCards();
      setModalOpen(false);
      setGiftCardAmount(0);
    } catch (error) {
      setMessage(error.response?.data?.message || "Error adding gift card");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (giftCard) => {
    setSelectedGiftCard(giftCard);
    setGiftCardAmount(giftCard.amount); // Initialize giftCardAmount
    setFormData({
      code: giftCard.code,
      amount: giftCard.amount,
      expiryDate: new Date(giftCard.expiryDate),
      isActive: giftCard.isActive,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await axios.put(`${baseUrl}/api/giftcards/update/${selectedGiftCard._id}`, formData);
      setMessage(response.data.message);
      fetchGiftCards();
      setEditModalOpen(false);

      // Reset form data and giftCardAmount after successful submission
      setFormData({
        code: "",
        amount: "",
        expiryDate: new Date(),
        isActive: true,
      });
      setGiftCardAmount(0);
    } catch (error) {
      setMessage(error.response?.data?.message || "Error updating gift card");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-content bg-white">
      <div className="container mx-auto p-6">
        <Breadcrumbs title="Estarch" breadcrumbItem="Gift Cards" />

        <div className="shadow-lg p-6 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xl font-bold">Gift Cards</p>
            <button
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              onClick={() => setTypeModalOpen(true)}
            >
              + Add Gift Card Type
            </button>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={() => setModalOpen(true)}
            >
              + Add Gift Card
            </button>
          </div>

          {loading ? (
            <p className="text-center text-gray-500">Loading gift cards...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-300">
                <thead className="bg-gray-200">
                  <tr className="border-b">
                    <th className="p-3">#</th>
                    <th className="p-3">Code</th>
                    <th className="p-3">Amount ($)</th>
                    <th className="p-3">Expiry Date</th>
                    <th className="p-3">Active</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {giftCards.length > 0 ? (
                    giftCards.map((giftCard, index) => (
                      <tr key={giftCard._id} className="border-b">
                        <td className="p-3 text-center">{index + 1}</td>
                        <td className="p-3">{giftCard.code} ({giftCard.usageCount || 0})</td>
                        <td className="p-3">${giftCard.amount}</td>
                        <td className="p-3">{new Date(giftCard.expiryDate).toLocaleDateString()}</td>
                        <td className="p-3 text-center">
                          {giftCard.isActive ? "✅ Active" : "❌ Inactive"}
                        </td>
                        <td className="p-3">
                          <button
                            className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                            onClick={() => handleEdit(giftCard)}
                          >
                            ✏️ Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center p-3 text-gray-500">No gift cards available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Add Gift Card</h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-600 hover:text-gray-900">&times;</button>
            </div>
            <div className="p-4">
              {message && <div className="p-3 mb-3 text-white bg-green-500 rounded">{message}</div>}
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="block font-semibold">Gift Card Code</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div className="mb-3">
                  <label className="block font-semibold">Amount ($)</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={giftCardAmount}
                    onChange={(e) => handleGiftCardTypesChange(e.target.value)}
                  >
                    <option value="">Select Amount</option>
                    {giftCardTypes.map((g) => (
                      <option key={g._id} value={g.amount}>
                        {g.amount}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="block font-semibold">Expiry Date</label>
                  <div className="border p-2 rounded">
                    <DatePicker
                      selected={formData.expiryDate}
                      onChange={handleDateChange}
                      dateFormat="yyyy-MM-dd"
                      required
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="mb-3 flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={() => setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
                    className="mr-2"
                  />
                  <label>Active Status</label>
                </div>

                <button
                  type="submit"
                  className="bg-blue-600 text-white w-full py-2 rounded hover:bg-blue-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Adding..." : "Create Gift Card"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Edit Gift Card</h2>
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  // Reset form data and giftCardAmount when closing the modal
                  setFormData({
                    code: "",
                    amount: "",
                    expiryDate: new Date(),
                    isActive: true,
                  });
                  setGiftCardAmount(0);
                }}
                className="text-gray-600 hover:text-gray-900"
              >
                &times;
              </button>
            </div>
            <div className="p-4">
              {message && <div className="p-3 mb-3 text-white bg-green-500 rounded">{message}</div>}
              <form onSubmit={handleEditSubmit}>
                <div className="mb-3">
                  <label className="block font-semibold">Gift Card Code</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div className="mb-3">
                  <label className="block font-semibold">Amount ($)</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={giftCardAmount}
                    onChange={(e) => handleGiftCardTypesChange(e.target.value)}
                  >
                    <option value={formData.amount}>{formData.amount}</option>
                    {giftCardTypes.map((g) => (
                      <option key={g._id} value={g.amount}>
                        {g.amount}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="block font-semibold">Expiry Date</label>
                  <div className="border p-2 rounded">
                    <DatePicker
                      selected={formData.expiryDate}
                      onChange={handleDateChange}
                      dateFormat="yyyy-MM-dd"
                      required
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="bg-green-600 text-white w-full py-2 rounded hover:bg-green-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Gift Card"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {typeModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Add Gift Card Type</h2>
              <button onClick={() => setTypeModalOpen(false)} className="text-gray-600 hover:text-gray-900">&times;</button>
            </div>
            <div className="p-4">
              {message && <div className="p-3 mb-3 text-white bg-green-500 rounded">{message}</div>}
              <form onSubmit={handleTypeSubmit}>
                <div className="mb-3">
                  <label className="block font-semibold">Amount ($)</label>
                  <input
                    type="number"
                    name="amount"
                    value={typeFormData.amount}
                    onChange={(e) => setTypeFormData({ amount: e.target.value })}
                    required
                    min="1"
                    className="w-full p-2 border rounded"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-green-600 text-white w-full py-2 rounded hover:bg-green-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Adding..." : "Create Gift Card Type"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftCardPage;